import React, { useRef, useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useImageSequence } from '../hooks/useImageSequence';

// ─────────────────────────────────────────────────────────────
// CONFIG
// ─────────────────────────────────────────────────────────────
const TOTAL_FRAMES = 147;
const FRAME_PATH = '/poster_reveal/';
const getFrameSrc = (i: number) =>
  `${FRAME_PATH}${String(i + 1).padStart(5, '0')}.webp`;

// Lerp factor — controls smoothness (lower = smoother but laggier, higher = snappier)
const LERP_FACTOR = 0.15;

// Story overlays — cinematic text beats
const STORY_BEATS = [
  {
    start: 0.20,
    end: 0.35,
    align: 'left' as const,
    heading: 'More than décor.',
    subtext: 'A statement in every frame.',
  },
  {
    start: 0.50,
    end: 0.65,
    align: 'right' as const,
    heading: 'Built to reveal.',
    subtext: 'Every layer, every detail, every vibe.',
  },
  {
    start: 0.80,
    end: 0.95,
    align: 'center' as const,
    heading: 'Own the wall.',
    subtext: 'Explore the drop.',
  },
];

// ─────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────
interface PosterRevealHeroProps {
  shouldLoad?: boolean;
}

const PosterRevealHero: React.FC<PosterRevealHeroProps> = ({ shouldLoad = true }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);

  // Smooth animation state (all kept in refs to avoid re-renders)
  const targetFrameRef = useRef(0);      // where scroll says we should be
  const currentFrameSmooth = useRef(0);  // smoothly interpolated current position
  const lastDrawnFrame = useRef(-1);     // last integer frame drawn to canvas
  const targetProgressRef = useRef(0);   // raw scroll progress target
  const currentProgressSmooth = useRef(0); // smoothly interpolated progress

  const [activeBeat, setActiveBeat] = useState<number | null>(null);

  // Image sequence preloading (progressive)
  const { loadedFrames, isLoaded, loadProgress } = useImageSequence(TOTAL_FRAMES, getFrameSrc, shouldLoad);

  // Check if first frame is ready
  const isFirstFrameReady = loadedFrames.has(0);

  // ────────────────────────────
  // Image Swapping - Hardware accelerated
  // ────────────────────────────
  const drawFrame = useCallback((targetIndex: number) => {
    if (!imgRef.current) return;

    const clampedIndex = Math.min(Math.max(targetIndex, 0), TOTAL_FRAMES - 1);
    
    // Find closest loaded frame (fallback logic for hyper-fast scrolling during progressive load)
    let safeIndex = clampedIndex;
    if (!loadedFrames.has(clampedIndex)) {
      // Find the nearest frame that *is* loaded by scanning backwards
      for (let i = clampedIndex; i >= 0; i--) {
        if (loadedFrames.has(i)) {
          safeIndex = i;
          break;
        }
      }
    }

    // Force browser to swap the hardware composite layer source
    imgRef.current.src = getFrameSrc(safeIndex);
  }, [loadedFrames]);

  // ────────────────────────────
  // Scroll handler — updates the target frame (the RAF loop reads it)
  // ────────────────────────────
  useEffect(() => {
    const handleScroll = () => {
      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const windowH = window.innerHeight;
      const scrollable = container.offsetHeight - windowH;
      const scrolled = -rect.top;
      const progress = Math.max(0, Math.min(1, scrolled / scrollable));

      // Update targets — the RAF loop will smoothly interpolate toward these
      targetFrameRef.current = progress * (TOTAL_FRAMES - 1);
      targetProgressRef.current = progress;

      // Determine active story beat
      let found: number | null = null;
      for (let i = 0; i < STORY_BEATS.length; i++) {
        if (progress >= STORY_BEATS[i].start && progress <= STORY_BEATS[i].end) {
          found = i;
          break;
        }
      }
      setActiveBeat((prev) => (prev === found ? prev : found));
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ────────────────────────────
  // Always-running RAF loop — lerps current frame toward target each tick
  // ────────────────────────────
  useEffect(() => {
    // Only start loop once we have at least the first frame to show
    if (!isFirstFrameReady) return;

    // Draw the initial frame immediately
    drawFrame(0);
    lastDrawnFrame.current = 0;

    let rafId: number;

    const tick = () => {
      const target = targetFrameRef.current;
      const current = currentFrameSmooth.current;
      const diff = target - current;

      // Lerp toward target
      if (Math.abs(diff) > 0.01) {
        currentFrameSmooth.current += diff * LERP_FACTOR;
      } else {
        currentFrameSmooth.current = target;
      }

      // Draw when the integer frame changes
      const frameIdx = Math.min(
        Math.max(Math.round(currentFrameSmooth.current), 0),
        TOTAL_FRAMES - 1
      );
      if (frameIdx !== lastDrawnFrame.current) {
        lastDrawnFrame.current = frameIdx;
        drawFrame(frameIdx);
      }

      // Smooth progress bar via direct DOM update
      const pDiff = targetProgressRef.current - currentProgressSmooth.current;
      if (Math.abs(pDiff) > 0.0001) {
        currentProgressSmooth.current += pDiff * LERP_FACTOR;
      } else {
        currentProgressSmooth.current = targetProgressRef.current;
      }
      if (progressBarRef.current) {
        progressBarRef.current.style.width = `${currentProgressSmooth.current * 100}%`;
      }

      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(rafId);
  }, [isFirstFrameReady, drawFrame]);

  // ────────────────────────────
  // Alignment helper
  // ────────────────────────────
  const getAlignmentClasses = (align: 'left' | 'right' | 'center') => {
    switch (align) {
      case 'left':
        return 'items-start text-left pl-8 md:pl-20 lg:pl-32';
      case 'right':
        return 'items-end text-right pr-8 md:pr-20 lg:pr-32';
      default:
        return 'items-center text-center';
    }
  };

  // ─────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────
  return (
    <div
      ref={containerRef}
      className="relative bg-black"
      style={{ height: '400vh' }}
    >
      {/* ── Sticky Viewport ── */}
      <div
        className="sticky top-0 left-0 w-full h-screen overflow-hidden bg-black flex items-center justify-center"
      >
        {/* ── Background Img Element (replaces Canvas) ── */}
        <img
          ref={imgRef}
          className="absolute top-0 left-0 w-full h-full object-contain md:object-cover object-center transition-opacity duration-500"
          style={{ 
            opacity: isFirstFrameReady ? 1 : 0,
            willChange: 'src' // hint to browser to optimize texture swapping
          }}
          alt="Poster Reveal Animation"
          draggable={false}
        />

        {/* ── Story Overlays ── */}
        {isFirstFrameReady &&
          STORY_BEATS.map((beat, i) => {
            const isActive = activeBeat === i;
            return (
              <motion.div
                key={i}
                className={`absolute inset-0 z-10 flex flex-col justify-center pointer-events-none px-6 ${getAlignmentClasses(beat.align)}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: isActive ? 1 : 0 }}
                transition={{ duration: 0.5, ease: 'easeInOut' }}
              >
                <div
                  className="absolute inset-0 pointer-events-none transition-colors duration-500"
                  style={{
                    background: isActive
                      ? 'radial-gradient(ellipse at center, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.15) 60%, transparent 100%)'
                      : 'transparent',
                  }}
                />
                <div className="relative z-10 max-w-2xl">
                  <motion.h2
                    className="text-4xl md:text-6xl lg:text-7xl font-black tracking-tighter text-white mb-3 leading-[0.95]"
                    initial={{ y: 20, opacity: 0 }}
                    animate={isActive ? { y: 0, opacity: 1 } : { y: 20, opacity: 0 }}
                    transition={{ duration: 0.45, delay: 0.08, ease: 'easeOut' }}
                  >
                    {beat.heading}
                  </motion.h2>
                  <motion.p
                    className="text-xs md:text-sm uppercase tracking-[0.25em] text-white/50 font-bold"
                    initial={{ y: 14, opacity: 0 }}
                    animate={isActive ? { y: 0, opacity: 1 } : { y: 14, opacity: 0 }}
                    transition={{ duration: 0.45, delay: 0.18, ease: 'easeOut' }}
                  >
                    {beat.subtext}
                  </motion.p>
                </div>
              </motion.div>
            );
          })}

        {/* ── Bottom progress bar ── */}
        <div
          ref={progressBarRef}
          className="absolute bottom-0 left-0 height-2 bg-amber-500/60 z-30"
          style={{
            height: 2,
            width: '0%',
            willChange: 'width',
          }}
        />
      </div>
    </div>
  );
};

export default PosterRevealHero;
